import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Info, ChevronDown } from 'lucide-react';
export function BusinessModel() {
  const [showAssumptions, setShowAssumptions] = useState(false);
  return <section id="pricing" className="py-24 border-b border-stone-300" data-id="element-796">
      <div className="max-w-6xl mx-auto px-6" data-id="element-797">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-798">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-799">
            {/* Left: Header */}
            <div className="lg:col-span-5" data-id="element-800">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-801">
                Business Model
              </p>

              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1] text-stone-900 mb-6" data-id="element-802">
                We win when you win
              </h2>

              <p className="text-2xl text-red-600 font-mono mb-6" data-id="element-803">
                5% of verified net savings
              </p>

              <p className="text-lg text-stone-600 leading-relaxed mb-8" data-id="element-804">
                We deploy the platform free or near-free. We establish a
                baseline from your trailing 12 months. We measure the wholesale
                leakage eliminated. We take 5% of the verified net savings.
                That's it.
              </p>

              <div className="space-y-3 text-sm text-stone-500" data-id="element-805">
                <div className="flex items-start gap-2" data-id="element-806">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" data-id="element-807" />
                  <span data-id="element-808">
                    <strong className="text-stone-700" data-id="element-809">Savings</strong> = Group
                    Room Revenue × Wholesale Leakage % × Adoption %
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-810">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" data-id="element-811" />
                  <span data-id="element-812">
                    <strong className="text-stone-700" data-id="element-813">BQ Share</strong> =
                    Savings × 5%
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Financial breakdown */}
            <div className="lg:col-span-7" data-id="element-814">
              <div className="border border-stone-300" data-id="element-815">
                <div className="p-6 border-b border-stone-300 bg-stone-100" data-id="element-816">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-817">
                    Financial Example — Base Case
                  </p>
                </div>

                {/* Inputs */}
                <div className="p-6 border-b border-stone-300" data-id="element-818">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-819">
                    Inputs
                  </p>
                  <div className="grid grid-cols-3 gap-4 font-mono text-sm" data-id="element-820">
                    <div data-id="element-821">
                      <p className="text-stone-500 mb-1" data-id="element-822">Group Room Revenue</p>
                      <p className="text-stone-900 text-lg" data-id="element-823">$50M</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-824">
                        Annual group bookings
                      </p>
                    </div>
                    <div data-id="element-825">
                      <p className="text-stone-500 mb-1" data-id="element-826">Wholesale Leakage</p>
                      <p className="text-stone-900 text-lg" data-id="element-827">20%</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-828">
                        Revenue lost to wholesalers
                      </p>
                    </div>
                    <div data-id="element-829">
                      <p className="text-stone-500 mb-1" data-id="element-830">Adoption</p>
                      <p className="text-stone-900 text-lg" data-id="element-831">60%</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-832">
                        Volume routed via BlockQuote
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-stone-200" data-id="element-833">
                    <p className="font-mono text-xs text-stone-400 mb-2" data-id="element-834">
                      $50M × 20% × 60% =
                    </p>
                    <p className="font-mono text-2xl text-stone-900" data-id="element-835">
                      $6.0M{' '}
                      <span className="text-sm text-stone-500" data-id="element-836">
                        estimated annual savings
                      </span>
                    </p>
                  </div>
                </div>

                {/* With BlockQuote */}
                <div className="p-6 border-b border-stone-300" data-id="element-837">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-838">
                    With BlockQuote
                  </p>
                  <div className="grid grid-cols-2 gap-8" data-id="element-839">
                    <div data-id="element-840">
                      <p className="font-mono text-xs text-stone-500 mb-2" data-id="element-841">
                        Resort Keeps (95%)
                      </p>
                      <p className="font-mono text-3xl text-stone-900" data-id="element-842">$5.7M</p>
                      <p className="text-sm text-stone-500 mt-1" data-id="element-843">
                        Pure profit to bottom line
                      </p>
                    </div>
                    <div data-id="element-844">
                      <p className="font-mono text-xs text-stone-500 mb-2" data-id="element-845">
                        BlockQuote Fee (5%)
                      </p>
                      <p className="font-mono text-3xl text-red-600" data-id="element-846">$300K</p>
                      <p className="text-sm text-stone-500 mt-1" data-id="element-847">
                        Only on verified savings
                      </p>
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div className="p-4 bg-stone-50" data-id="element-848">
                  <p className="font-mono text-xs text-stone-500" data-id="element-849">
                    * Example per resort group. Scales with portfolio size.
                  </p>
                </div>
              </div>

              {/* Assumptions Toggle */}
              <button onClick={() => setShowAssumptions(!showAssumptions)} className="mt-4 flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors" data-id="element-850">
                <ChevronDown className={`w-4 h-4 transition-transform ${showAssumptions ? 'rotate-180' : ''}`} data-id="element-851" />
                Assumptions
              </button>
              {showAssumptions && <div className="mt-3 p-4 bg-stone-50 border border-stone-200 text-sm text-stone-500 space-y-2" data-id="element-852">
                  <p data-id="element-853">
                    • Savings are calculated from verified leakage reduction on
                    group room revenue.
                  </p>
                  <p data-id="element-854">
                    • Adoption % reflects the portion of group volume routed
                    through BlockQuote.ai.
                  </p>
                  <p data-id="element-855">
                    • Actual results vary by seasonality, segment, and
                    integration depth.
                  </p>
                </div>}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}